import { useEffect, useState } from 'react';
import axios from 'axios';
import { AkorepsWordmark } from '../components/AkorepsWordmark';
import api from '../lib/api';

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
/** Preview SPA targets per-PR API; OAuth start stays on fixed preview edge. */
const authBase = (import.meta.env.VITE_AUTH_URL || apiUrl).replace(/\/+$/, '');
const previewPr = import.meta.env.VITE_PREVIEW_PR_NUMBER?.trim();
const ssoQuery = previewPr ? `?pr=${encodeURIComponent(previewPr)}` : '';
const TERMS_URL = import.meta.env.VITE_TERMS_URL || 'https://akoreps.com/terms';
const PRIVACY_URL = import.meta.env.VITE_PRIVACY_URL || 'https://akoreps.com/privacy';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  const getApiErrorMessage = (err: unknown, fallback: string) => {
    if (axios.isAxiosError<{ error?: string }>(err)) {
      return err.response?.data?.error || fallback;
    }
    return fallback;
  };

  useEffect(() => {
    if (!sent || resendCooldown <= 0) return;

    const intervalId = window.setInterval(() => {
      setResendCooldown((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [sent, resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/auth/magic-link', { email });
      setSent(true);
      setResendCooldown(60);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Something went wrong'));
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending) return;

    setError('');
    setIsResending(true);
    try {
      await api.post('/auth/magic-link', { email });
      setResendCooldown(60);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Failed to resend magic link'));
    } finally {
      setIsResending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white p-6 sm:p-8 rounded-lg shadow-sm border">
          <div className="text-center">
            <div className="text-4xl mb-4">✉️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h2>
            <p className="text-gray-600">
              We sent a magic link to <strong>{email}</strong>. Click the link to sign in.
            </p>
            {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0 || isResending}
              className="mt-6 w-full bg-green-700 text-white py-2 px-4 rounded-md font-medium hover:bg-green-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isResending
                ? 'Sending...'
                : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : 'Resend magic link'}
            </button>
            <p className="mt-3 text-sm text-gray-500">
              Didn&apos;t get it? Check your spam folder or resend once the timer is up.
            </p>
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setError('');
              }}
              className="mt-4 text-sm text-green-700 hover:text-green-900 font-medium transition-colors"
            >
              ← Use a different sign-in method
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white p-6 sm:p-8 rounded-lg shadow-sm border">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="flex justify-center" data-testid="login-brand-heading">
            <span className="sr-only">Akoreps</span>
            <AkorepsWordmark className="h-8 sm:h-9 w-auto" aria-hidden />
          </h1>
          <p className="text-gray-600 mt-2" data-testid="login-value-prop-primary">
            Sign in or create your account
          </p>
          <p className="text-sm text-gray-500 mt-1" data-testid="login-value-prop-secondary">
            Use your work email. If you are new here, we'll create your account automatically when
            you finish signing in.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Work email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            className="w-full bg-green-700 text-white py-2 px-4 rounded-md font-medium hover:bg-green-800 transition-colors"
          >
            Send magic link
          </button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white px-2 text-gray-500">or</span>
          </div>
        </div>

        <div className="space-y-3">
          <a
            href={`${authBase}/auth/sso/google${ssoQuery}`}
            className="w-full flex items-center justify-center gap-3 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </a>

          <a
            href={`${authBase}/auth/sso/microsoft${ssoQuery}`}
            className="w-full flex items-center justify-center gap-3 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 21 21">
              <rect fill="#F25022" x="1" y="1" width="9" height="9" />
              <rect fill="#7FBA00" x="11" y="1" width="9" height="9" />
              <rect fill="#00A4EF" x="1" y="11" width="9" height="9" />
              <rect fill="#FFB900" x="11" y="11" width="9" height="9" />
            </svg>
            Continue with Microsoft
          </a>

          <a
            href={`${authBase}/auth/sso/apple${ssoQuery}`}
            className="w-full flex items-center justify-center gap-3 px-4 py-2 border border-gray-900 rounded-md text-sm font-medium text-white bg-black hover:bg-gray-900 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.52-3.23 0-1.44.64-2.2.45-3.06-.4C3.79 16.17 4.36 10.1 8.71 9.86c1.23.06 2.09.7 2.81.73.99-.2 1.94-.78 3-.84 1.27-.1 2.24.38 2.87 1.14-2.62 1.57-2 5.02.38 5.99-.46 1.2-.89 2.02-1.72 3.4zM12.03 9.8c-.15-2.34 1.84-4.36 4.02-4.55.28 2.62-2.34 4.6-4.02 4.55z" />
            </svg>
            Continue with Apple
          </a>

          <a
            href={`${authBase}/auth/sso/linkedin${ssoQuery}`}
            className="w-full flex items-center justify-center gap-3 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0A66C2">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            Continue with LinkedIn
          </a>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          By signing in you agree to our{' '}
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-700 hover:text-green-900 underline"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-700 hover:text-green-900 underline"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
